import { useEffect, useMemo, useRef, useState } from 'react'
import type { TimelineElement } from '../../context/TimelineContext'
import type { Workspace, WorkspaceRole } from '../../types/workspace'
import { isModuleEnabled } from '../../utils/workspaceModules'
import { listEduEvents, type EduEventItem } from '../../services/eduEventService'
import { listEduPtmDays, listEduPtmSlots } from '../../services/eduPtmService'
import { utcIsoToZonedParts, resolveWorkspaceTimezone } from '../../utils/eduZonedTime'
import { isDayVisibleToStaff, getTeachersForPtmDayAudience } from '../Education/eduPtmHelpers'
import { getNlsString } from '../../nls/nlsRuntime'
import type { CalendarEvent } from './calendarHelpers'

export interface UseEduCalendarEventsOptions {
  workspaceId?: string | null
  uid?: string | null
  windowStart: string
  windowEnd: string
  mode: 'MINE' | 'ALL' | 'OFF'
  isRestricted?: boolean
  role?: WorkspaceRole
  workspace?: Pick<Workspace, 'modules'> | null
  elements?: TimelineElement[]
  refreshKey?: number
}

async function fetchAllPages(
  query: Parameters<typeof listEduEvents>[0],
  onWarnCap?: () => void,
): Promise<EduEventItem[]> {
  const accumulated: EduEventItem[] = []
  let cursor: string | undefined = undefined

  while (true) {
    const res = await listEduEvents({
      ...query,
      cursor,
    })
    accumulated.push(...res.events)
    if (accumulated.length >= 500) {
      onWarnCap?.()
      return accumulated.slice(0, 500)
    }
    if (!res.nextCursor) {
      break
    }
    cursor = res.nextCursor
  }
  return accumulated
}

export function useEduCalendarEvents({
  workspaceId,
  uid,
  windowStart,
  windowEnd,
  mode,
  isRestricted,
  role,
  workspace,
  elements = [],
  refreshKey,
}: UseEduCalendarEventsOptions): CalendarEvent[] {
  const [events, setEvents] = useState<CalendarEvent[]>([])

  const taughtClassIds = useMemo(() => {
    if (!elements || !uid) return []
    return elements
      .filter((el) => {
        const isClass =
          el.edu?.kind === 'CLASS' ||
          el.edu?.kind === 'GROUP' ||
          el.edu?.kind === 'CLIENT'
        return isClass && Boolean(el.edu?.teachers?.[uid])
      })
      .map((el) => el.id)
      .sort()
  }, [elements, uid])

  const taughtClassIdsKey = taughtClassIds.join(',')

  const isTeaching = isModuleEnabled(workspace as Workspace | null | undefined, 'teaching')
  const isTraining = isModuleEnabled(workspace as Workspace | null | undefined, 'training')
  const isEduEnabled = isTeaching || isTraining

  const elementsRef = useRef<TimelineElement[]>(elements || [])
  useEffect(() => {
    elementsRef.current = elements || []
  }, [elements])

  const resolvedTimezone = resolveWorkspaceTimezone(workspace as Workspace | null | undefined)

  const audienceStructureKey = useMemo(() => {
    if (!elements) return ''
    const parts: string[] = []
    for (const el of elements) {
      const isAudienceRelevant =
        el.edu?.kind === 'CLASS' ||
        el.edu?.kind === 'GROUP' ||
        el.edu?.kind === 'CLIENT' ||
        el.edu?.kind === 'YEAR' ||
        el.edu?.kind === 'PROGRAMME'
      if (!isAudienceRelevant) continue
      const teacherUids = el.edu?.teachers ? Object.keys(el.edu.teachers).sort().join(',') : ''
      parts.push(`${el.id}:${el.parentId || ''}:${teacherUids}`)
    }
    return parts.sort().join(';')
  }, [elements])

  useEffect(() => {
    let isCancelled = false

    const isUnrestrictedAdmin =
      !isRestricted && (role === 'ADMIN' || role === 'OWNER')
    const effectiveMode = mode === 'ALL' && isUnrestrictedAdmin ? 'ALL' : 'MINE'

    const fetchEvents = async () => {
      if (!isEduEnabled || mode === 'OFF' || !workspaceId || !uid) {
        if (!isCancelled) {
          setEvents([])
        }
        return
      }

      try {
        let rawEvents: EduEventItem[] = []
        let warnedCap = false
        const warnOnce = () => {
          if (!warnedCap) {
            console.warn('[useEduCalendarEvents] Cap of 500 events reached')
            warnedCap = true
          }
        }

        if (effectiveMode === 'ALL') {
          rawEvents = await fetchAllPages(
            {
              workspaceId,
              from: windowStart,
              to: windowEnd,
            },
            warnOnce,
          )
        } else {
          if (taughtClassIds.length === 0) {
            if (!isCancelled) setEvents([])
            return
          }

          const results = await Promise.all(
            taughtClassIds.map((clsId) =>
              fetchAllPages(
                {
                  workspaceId,
                  nodeId: clsId,
                  from: windowStart,
                  to: windowEnd,
                },
                warnOnce,
              ),
            ),
          )

          const dedupedMap = new Map<string, EduEventItem>()
          for (const res of results) {
            for (const evt of res) {
              if (!dedupedMap.has(evt.id)) {
                if (dedupedMap.size >= 500) {
                  warnOnce()
                  break
                }
                dedupedMap.set(evt.id, evt)
              }
            }
          }
          rawEvents = Array.from(dedupedMap.values())
        }

        if (isCancelled) return

        const calEvents: CalendarEvent[] = rawEvents.map((evt) => {
          const tz = evt.timezone || 'Asia/Kolkata'
          const startParts = utcIsoToZonedParts(evt.startAt, tz)
          const endParts = utcIsoToZonedParts(evt.endAt, tz)

          const startDate = startParts.date
          const endDate = endParts.date
          const startTime = evt.allDay ? null : startParts.time
          const endTime = evt.allDay ? null : endParts.time

          return {
            id: `edu-event-${evt.id}`,
            kind: 'edu_event' as const,
            title: evt.title,
            startDate,
            endDate,
            startTime,
            endTime,
            rootGoalId: null,
            parentId: null,
            board: 'education',
            eduEventId: evt.id,
            eduEvent: evt,
            isCancelled: evt.status === 'CANCELLED',
          }
        })

        const ptmCalEvents: CalendarEvent[] = []
        try {
          const ptmRes = await listEduPtmDays({ workspaceId })
          const ptmDays = ptmRes.days || []

          const windowStartDate = windowStart.slice(0, 10)
          const windowEndDate = windowEnd.slice(0, 10)

          const visiblePtmDays = ptmDays.filter((day) => {
            if (day.date < windowStartDate || day.date > windowEndDate) return false
            return isDayVisibleToStaff(day, {
              isAdmin: effectiveMode === 'ALL',
              currentUserId: uid,
              elements: elementsRef.current,
            })
          })

          for (const day of visiblePtmDays) {
            // Add PTM Day as all-day event
            ptmCalEvents.push({
              id: `edu-ptm-day-${day.id}`,
              kind: 'edu_ptm' as const,
              title: getNlsString('NLSEduPtmCalendarTitle', { title: day.title }),
              startDate: day.date,
              endDate: day.date,
              startTime: null,
              endTime: null,
              rootGoalId: null,
              parentId: null,
              board: 'education',
              ptmDayId: day.id,
              ptmDay: day,
              isCancelled: day.status === 'CANCELLED',
            })

            // Booked slots
            const teacherUidsToFetch: string[] = []
            if (effectiveMode === 'ALL') {
              const teachers = getTeachersForPtmDayAudience(day.audience, elementsRef.current)
              if (teachers.length > 0) {
                teachers.forEach((t) => teacherUidsToFetch.push(t.uid))
              } else {
                teacherUidsToFetch.push(uid)
              }
            } else {
              teacherUidsToFetch.push(uid)
            }

            const slotResults = await Promise.all(
              teacherUidsToFetch.map((tUid) =>
                listEduPtmSlots({
                  workspaceId,
                  dayId: day.id,
                  teacherUid: tUid,
                }).catch(() => ({ slots: [] })),
              ),
            )

            for (let i = 0; i < slotResults.length; i++) {
              const tUid = teacherUidsToFetch[i]
              const sRes = slotResults[i]
              for (const slot of sRes.slots || []) {
                if (slot.booking && slot.booking.status !== 'CANCELLED') {
                  const startParts = utcIsoToZonedParts(slot.slotStart, resolvedTimezone)
                  const endParts = utcIsoToZonedParts(slot.slotEnd, resolvedTimezone)

                  ptmCalEvents.push({
                    id: `edu-ptm-slot-${slot.booking.id}`,
                    kind: 'edu_ptm' as const,
                    title: getNlsString('NLSEduPtmCalendarTitle', { title: slot.booking.learnerName }),
                    startDate: startParts.date,
                    endDate: endParts.date,
                    startTime: startParts.time,
                    endTime: endParts.time,
                    rootGoalId: null,
                    parentId: null,
                    board: 'education',
                    ptmDayId: day.id,
                    ptmDay: day,
                    ptmSlot: slot,
                    ptmTeacherUid: tUid,
                    isCancelled: false,
                  })
                }
              }
            }
          }
        } catch {
          // Fallback if PTM list fails
        }

        if (!isCancelled) {
          setEvents([...calEvents, ...ptmCalEvents])
        }
      } catch {
        if (!isCancelled) {
          setEvents([])
        }
      }
    }

    void fetchEvents()

    return () => {
      isCancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- guarded by taughtClassIdsKey & audienceStructureKey
  }, [
    workspaceId,
    uid,
    windowStart,
    windowEnd,
    mode,
    isRestricted,
    role,
    isEduEnabled,
    taughtClassIdsKey,
    audienceStructureKey,
    resolvedTimezone,
    refreshKey,
  ])

  return events
}
